import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { startAheadFor } from './settingsUtils';
import {
  CalendarEvent,
  CalendarEventType,
  CushionTaskDto,
  Formats,
  PageRange,
  ReadingSourceInfoDto,
  Subtask,
  TaskCategoryDto,
  TaskState,
  UntilOption,
  ViewPlannedTask,
  WorkloadTask
} from 'shovel-lib/types';
import { DATE_TIME_FORMAT, formatMomentDateAs, now, toMomentDate, toMomentUtcDate } from 'shovel-lib/utils/timeUtils';
import { secondsToDurationInMinutes } from './filters';
import { calculateNewAvgTimePerPage } from './timer';
import t from '../i18n/t';
import colors from './colors';
import { ResizeTaskType } from '../state/task/types';
import { capitalize } from './textUtils';
import { PileSections } from '../state/pile/types';
import linkifyHtml from 'linkifyjs/html';
import DOMPurify from 'dompurify';
import { RRule, WeekdayStr } from 'rrule';
import { FormFieldName } from './enums';
import { deadlineDay } from './dayLabel';

export const dueDatePreview = (date: any, state: TaskState, planned?: boolean): string => {
  // if (planned) {
  //   return `${t.DUE}: ${formatMomentDateAs(toMomentDate(date), Formats.MONTH_DAY_FORMAT)}`;
  // }
  // const format = state === TaskState.ACTIVE ? Formats.TIME : Formats.DATE_TIME_FORMAT;
  return formatMomentDateAs(toMomentDate(date), Formats.DATE_TIME_FORMAT);
};

export const pageRangesToAdditionalText = (pageRanges: PageRange[]) =>
  pageRanges
    .map(pageRange => (pageRange.from && pageRange.to ? `${pageRange.from}-${pageRange.to}` : pageRange.pages))
    .join(', ');

export const sumPageRanges = (pageRanges: { from?: number; to?: number }[]) =>
  pageRanges
    .map(range => (range.to && range.from ? Math.abs(range.to - range.from) + 1 : 0))
    .reduce((sum, pages) => sum + pages, 0);

export const calculateNewAvgPerPage = (timeSpent?: number, pagesRead?: number) => {
  if (!timeSpent || !pagesRead) {
    return undefined;
  }
  return secondsToDurationInMinutes(calculateNewAvgTimePerPage(timeSpent, pagesRead));
};

export const ObviousMikeAnimation = styled.div<{ left?: number }>`
  position: absolute;
  top: -20px;
  left: calc(50% - ${props => `${props.left || 0}px`});
  animation: obviousMike 1s infinite;

  @keyframes obviousMike {
    0% {
      top: -20px;
    }
    50% {
      top: -25px;
    }
    100% {
      top: -20px;
    }
  }
`;

export const ObviousMike = styled.div`
  position: relative;
  &:hover {
    ${ObviousMikeAnimation} {
      animation: none;
    }
  }
`;

export const AUTOSAVE_INDICATOR_TIMEOUT = 800;

export const MARK_COMPLETE_ANIMATION_DURATION = 100;

export const ERROR_TIMEOUT = 3000;

export const CUSHION_CRITICAL_WARNING_BREAKPOINT_HOURS = 0;

export const getCushionItemColor = (cushion: number, timeNeed: number, warningPercentage: number) => {
  if (cushion < CUSHION_CRITICAL_WARNING_BREAKPOINT_HOURS) {
    return colors.negative;
  }
  if (cushion > CUSHION_CRITICAL_WARNING_BREAKPOINT_HOURS && cushion <= warningPercentage * timeNeed) {
    return colors.warningBulletColor;
  }

  return colors.positive;
};

export const timelineGroups = [{ id: 1, title: 'group' }];

export const mapCushionTask2TimelineItem = (
  tasks: Array<CushionTaskDto>,
  warningPercentage: number,
  resizeType: ResizeTaskType,
  colorByCushion: boolean
) => {
  return tasks.map(task => ({
    id: task.taskId.toString(),
    group: 1,
    name: task.title,
    start_time: toMomentDate(task.dueDate)
      .add(-task.daysToStartAhead, 'day')
      .startOf('day'),
    end_time: toMomentDate(task.dueDate),
    bgColor: getCushionItemColor(task.cushion, task.timeNeed, warningPercentage),
    cushion: task.cushion,
    type: task.type,
    canMove: false,
    canResize: resizeType,
    colorHex: task.colorHex,
    state: task.state,
    course: task.courseName,
    taskTitle: task.title,
    subtitle: task.subtitle,
    colorByCushion: colorByCushion,
    task
  }));
};
export const groupByDate = tasks => {
  const groups = { [PileSections.NO_DUE_DATE]: [] };
  tasks.forEach(tt => {
    // The day each deadline belongs to: one at 00:00 is the end of the day before, as its card and the calendar say.
    const date = tt.due ? deadlineDay(tt.due).toISOString() : PileSections.NO_DUE_DATE;
    if (date in groups) {
      groups[date].push(tt);
    } else {
      groups[date] = new Array(tt);
    }
  });
  if (groups[PileSections.NO_DUE_DATE].length === 0) {
    // @ts-ignore
    delete groups[PileSections.NO_DUE_DATE];
  }
  return groups;
};

export const deleteTaskCategoryFilter = (id: number) => {
  return id ? (c: TaskCategoryDto) => c.id !== id : (c: TaskCategoryDto) => c.id;
};

export const deleteReadingSourceFilter = (id: number) => {
  return id ? (c: ReadingSourceInfoDto) => c.id !== id : (c: ReadingSourceInfoDto) => c.id;
};

export const getMultipleTaskActionConfirmationMessage = (
  taskLength: number,
  prefix: string,
  suffix: string,
  numberFirst?: boolean
) =>
  `${prefix} ${numberFirst ? `${taskLength} ` : ''}${numberFirst ? t.SELECTED : capitalize(t.SELECTED)}${
    !numberFirst ? ` ${taskLength}` : ''
  } ${taskLength === 1 ? t.TASK?.toLowerCase() : t.TASKS?.toLowerCase()} ${suffix}`;

export const groupByDueDate = (tasks: CalendarEvent[]) => {
  const groups = {};
  tasks.forEach(task => {
    if (task.type === CalendarEventType.TASK) {
      const date = toMomentDate(task.start)
        .seconds(0)
        .milliseconds(0)
        .toISOString();
      if (date in groups) {
        groups[date].push(task);
      } else {
        groups[date] = new Array(task);
      }
    } else {
      groups[task.id!] = [task];
    }
  });
  return groups;
};

export const filterTasksBySearchText = (tasks?: WorkloadTask[], searchText?: string) => {
  if (!tasks) return [];
  if (!searchText) return tasks;
  return tasks.filter(
    task =>
      task.title.toLowerCase().includes(searchText.toLowerCase()) ||
      (task.details || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (task.courseName || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (task.taskCategory?.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (task.reading?.name || '').toLowerCase().includes(searchText.toLowerCase())
  );
};

export const getDDPosition = (id: string) => {
  const el = document.getElementById(id);
  const section = document.getElementById('addTaskSection');
  if (section && el) {
    const { width, left } = section.getBoundingClientRect();
    const { left: elLeft } = el.getBoundingClientRect();
    return left - elLeft > width / 2 ? 'bottom left' : 'bottom right';
  }
  return undefined;
};

export const parseTaskMetadata = (metadataString: string) => {
  return JSON.parse(metadataString.replace(/\n/g, '<br />'));
};

export const getTaskStatus = (dueDate, noDueDate, overdue) => {
  if (noDueDate) {
    return TaskState.ACTIVE;
  }
  if (overdue) {
    return TaskState.DO_LATER;
  }
  return dueDate ? (now().isBefore(dueDate) ? TaskState.ACTIVE : TaskState.DO_LATER) : TaskState.ACTIVE;
};

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Descriptions come from Google events (HTML) and LMS feeds (plain text, `\n` already turned into `<br />` by
 * parseTaskMetadata). linkifyHtml's tokenizer reads any `<` as a tag, so a rubric line like "<4/10: Several prompts…"
 * made it throw and, with nothing to catch it, blanked the whole app. A `<` that can't open a tag (not followed by a
 * letter, `/` or `!`) is plain text in HTML, so it's escaped first; anything still unreadable shows as plain text.
 *
 * Whoever wrote the description (anyone who sends the student a Google invite, a teacher in the LMS) also decides its
 * HTML, so only formatting, links, pictures and tables are kept: no scripts, event handlers, `javascript:` links or
 * styles.
 */
export const descriptionToHtml = (descriptionString: string) => {
  const options = { target: { url: '_blank' } };
  let html: string;
  try {
    html = linkifyHtml(descriptionString.replace(/<(?![a-zA-Z/!])/g, '&lt;'), options);
  } catch {
    const plain = escapeHtml(descriptionString).replace(/&lt;br\s*\/?&gt;/gi, '<br />');
    try {
      html = linkifyHtml(plain, options);
    } catch {
      html = plain;
    }
  }
  return DOMPurify.sanitize(html, DESCRIPTION_HTML);
};

const DESCRIPTION_HTML = {
  ALLOWED_TAGS: [
    'a',
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'br',
    'p',
    'div',
    'span',
    'ul',
    'ol',
    'li',
    'hr',
    'blockquote',
    'pre',
    'code',
    'h1',
    'h2',
    'h3',
    'h4',
    'sub',
    'sup',
    // Teachers show examples as pictures and rubrics as tables.
    'img',
    'table',
    'caption',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td'
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'colspan', 'rowspan']
};

// Every link in a description opens in a new tab, written into a Google invite or found by linkifyHtml alike, so it
// never takes the student away from Shovel. Pictures load only from the web (an LMS's own relative paths like
// "Images/Header.jpeg" never load in Shovel anyway) and don't tell their host which Shovel page showed them.
DOMPurify.addHook('afterSanitizeAttributes', node => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
  if (node.tagName === 'IMG') {
    if (!/^(https?:|data:image\/)/i.test(node.getAttribute('src')?.trim() ?? '')) node.parentNode?.removeChild(node);
    else node.setAttribute('referrerpolicy', 'no-referrer');
  }
});

/** Opens a link from a description's source (LMS feed, Google) only when it's a web address. */
export const openOutsideLink = (link?: string | null) => {
  if (link && /^https?:\/\//i.test(link.trim())) window.open(link.trim(), '_blank', 'noopener');
};

export const renderGoogleEventDescription = (descriptionString: string) => {
  if (!descriptionString) return null;
  return (
    <GoogleEventDescriptionSection>
      <div dangerouslySetInnerHTML={{ __html: descriptionToHtml(descriptionString) }} />
    </GoogleEventDescriptionSection>
  );
};

const GoogleEventDescriptionSection = styled.div`
  margin-bottom: 15px;
  font-family: 'Quicksand', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.textColor};

  a {
    color: #4285f4;
    text-decoration: none;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 4px 8px;
    border: 1px solid ${({ theme }) => `color-mix(in srgb, ${theme.textColor} 20%, transparent)`};
    text-align: left;
    vertical-align: top;
  }
`;

export const sumPagesForUnusedPlannedTasks = (plannedTasks: ViewPlannedTask[]) => {
  return plannedTasks.reduce(
    (acc, te) => acc + ((te.isUsed || toMomentDate(te.end).isBefore(now()) ? 0 : te.pages) || 0),
    0
  );
};

export type AddTaskState = {
  step: number;
  isRepeatSet: boolean;
  repeatOptions: {
    interval: number;
    onDays?: WeekdayStr[];
    until?: Date;
    untilOption: any;
    copyFields: {
      name: boolean;
      continueNumbering: boolean;
      description: boolean;
      subtasks: boolean;
      startAhead: boolean;
      pages: boolean;
      estimate: boolean;
    };
  };
  tasks: Array<any>;
  course: any;
  category: any;
  readingSource: any;
  task: {
    [FormFieldName.NAME]: string;
    [FormFieldName.DESCRIPTION]: string;
    [FormFieldName.COURSE]: any;
    [FormFieldName.SUBTASKS]: Subtask[];
    [FormFieldName.CATEGORY]: any;
    [FormFieldName.READING_SOURCE]: any;
    [FormFieldName.DUE_DATE]?: Date;
    [FormFieldName.START_AHEAD]: number;
    [FormFieldName.ESTIMATE]: number;
    [FormFieldName.PAGE_RANGES]: any;
    [FormFieldName.AUTO_ESTIMATE]: boolean;
  };
};

export const initialAddTaskState = {
  step: 1,
  isRepeatSet: false,
  repeatOptions: {
    interval: 1,
    onDays: [],
    until: undefined,
    untilOption: UntilOption.CLASSES,
    copyFields: {
      name: true,
      continueNumbering: true,
      description: false,
      subtasks: false,
      startAhead: true,
      pages: true,
      estimate: true
    }
  },
  tasks: [],
  course: null,
  category: null,
  readingSource: null,
  task: {
    [FormFieldName.NAME]: '',
    [FormFieldName.DESCRIPTION]: '',
    [FormFieldName.COURSE]: null,
    [FormFieldName.SUBTASKS]: [],
    [FormFieldName.CATEGORY]: null,
    [FormFieldName.READING_SOURCE]: null,
    [FormFieldName.DUE_DATE]: undefined,
    [FormFieldName.START_AHEAD]: 0,
    [FormFieldName.ESTIMATE]: 0,
    [FormFieldName.PAGE_RANGES]: [],
    [FormFieldName.AUTO_ESTIMATE]: false
  }
};

export const defaultRepeatOptions = {
  isRepeatSet: false,
  repeatOptions: {
    interval: 1,
    onDays: [],
    until: undefined,
    untilOption: UntilOption.CLASSES,
    copyFields: {
      name: true,
      continueNumbering: true,
      description: false,
      subtasks: false,
      startAhead: true,
      pages: true,
      estimate: true
    }
  }
};

// Repeat numbering: "Quiz 5" repeats as Quiz 5, Quiz 6, Quiz 7… - the number at the end of the name is replaced, never
// added to ("Quiz 5 5"), and a name without one gets it added ("Quiz" → Quiz 1, Quiz 2…).
const ENDING_NUMBER = /^(.*?)(\s*)(\d+)$/;

export const endingNumber = (name: string): number | null => {
  const match = name.trim().match(ENDING_NUMBER);
  return match ? Number(match[3]) : null;
};

export const withoutEndingNumber = (name: string) => {
  const match = name.trim().match(ENDING_NUMBER);
  return match ? match[1] : name.trim();
};

export const numberedTaskName = (name: string, number: number) => {
  const match = name.trim().match(ENDING_NUMBER);
  return match ? `${match[1]}${match[2]}${number}` : `${name.trim()} ${number}`;
};

// Whether repeats are numbered, and from what. A choice made in the Repeat dialog holds for the name it was made for;
// for any other name (none made yet, or the name changed since) it follows the name: on, from its number, when it ends
// in one - copying "Quiz 5" to every week is never what's meant - and off, from 1, when it doesn't.
export const repeatNumbering = (name: string, copyFields?: any): { numbered: boolean; from: number } => {
  if (copyFields && copyFields.numberedName === name) {
    return { numbered: !!copyFields.continueNumbering, from: Number(copyFields.numberFrom) || 0 };
  }
  const number = endingNumber(name);
  return { numbered: number !== null, from: number ?? 1 };
};

export function getRepeatTaskOptions(options: any, defaultUntil: any) {
  const { interval, onDays, until, untilOption, course, category, ...copyOptions } = options;
  const repeatOptions = {
    interval,
    onDays,
    until: until || defaultUntil,
    course,
    category,
    untilOption,
    copyFields: copyOptions
  };
  return { repeatOptions, isRepeatSet: true };
}

export function isDateInHoliday(date: Date, holidays: any[] | undefined) {
  const holiday = holidays?.find(holiday => date >= new Date(holiday.start) && date <= new Date(holiday.end));
  return holiday?.title;
}

export function generateTasksForRepeat(
  values: any,
  { repeatOptions: { until, interval, onDays, copyFields } }: any,
  { timezone: tzid, courses, activities, categories, readingSources, daysAhead, startsAheadByTimeNeeded }: any,
  isInHolidayFun: (Date) => boolean
) {
  if (!values[FormFieldName.DUE_DATE] || !until) return;

  const dtstart = toMomentUtcDate(moment(values[FormFieldName.DUE_DATE]).format(DATE_TIME_FORMAT)).toDate();

  const untilDate = toMomentUtcDate(formatMomentDateAs(toMomentDate(until).endOf('day'), DATE_TIME_FORMAT)).toDate();

  //@ts-ignore
  const rule = new RRule({
    freq: RRule.WEEKLY,
    interval: interval,
    tzid,
    dtstart,
    byweekday: onDays?.length! > 0 ? onDays : undefined,
    until: untilDate
  });

  const name = values[FormFieldName.NAME] || '';
  const numbering = repeatNumbering(name, copyFields);
  const numbered = copyFields.name && numbering.numbered && !!name.trim();
  // Weeks in a holiday aren't created unless ticked, so they don't use up a number.
  let nextNumber = numbering.from;

  const tasks = rule.all().map((date, index) => {
    const dueDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );

    const isInHoliday = isInHolidayFun(dueDate);

    const isFirstTask = index === 0;
    const reading = !!values[FormFieldName.READING_SOURCE];
    const estimate = (reading ? copyFields.pages : copyFields.estimate) || isFirstTask ? values[FormFieldName.ESTIMATE] : 0;
    const copiesStartAhead = copyFields.startAhead || isFirstTask;
    // Based on time needed, each week's Start ahead comes from its own estimate (the setting's days without one),
    // unless it was picked by hand.
    const startAhead = !copiesStartAhead
      ? 0
      : startsAheadByTimeNeeded && !values.startAheadSetByHand
      ? startAheadFor(estimate, daysAhead, true)
      : values[FormFieldName.START_AHEAD];
    const taskName = !numbered
      ? copyFields.name || isFirstTask
        ? name
        : ''
      : isInHoliday
      ? withoutEndingNumber(name)
      : numberedTaskName(name, nextNumber++);
    const task = {
      [FormFieldName.NAME]: taskName,
      [FormFieldName.DESCRIPTION]: copyFields.description || isFirstTask ? values[FormFieldName.DESCRIPTION] : '',
      [FormFieldName.START_AHEAD]: startAhead,
      startAheadSetByHand: copiesStartAhead ? values.startAheadSetByHand : undefined,
      [FormFieldName.SUBTASKS]: copyFields.subtasks || isFirstTask ? values[FormFieldName.SUBTASKS] : [],
      [FormFieldName.CATEGORY]: values[FormFieldName.CATEGORY],
      [FormFieldName.COURSE]: values[FormFieldName.COURSE],
      [FormFieldName.PRIORITY]: values[FormFieldName.PRIORITY],
      [FormFieldName.DUE_DATE]: dueDate,
      [FormFieldName.ESTIMATE]: estimate,
      [FormFieldName.AUTO_ESTIMATE]: values[FormFieldName.AUTO_ESTIMATE],
      [FormFieldName.DURATION]: values[FormFieldName.DURATION],
      holiday: isInHoliday,
      selected: !isInHoliday
    };
    return values[FormFieldName.READING_SOURCE]
      ? {
          ...task,
          [FormFieldName.READING_SOURCE]: values[FormFieldName.READING_SOURCE],
          [FormFieldName.PAGE_RANGES]: copyFields.pages || isFirstTask ? values[FormFieldName.PAGE_RANGES] : []
        }
      : task;
  });
  const course = [...courses, ...activities].find(c => c.id === values[FormFieldName.COURSE]);
  const taskCategories = categories || (course ? course.taskCategories : []);
  const readings = readingSources || (course ? course.readingSources : []);
  const category = taskCategories.find(c => c.id === values[FormFieldName.CATEGORY]);
  const readingSource = readings.find(rs => rs.id === values[FormFieldName.READING_SOURCE]);

  return {
    tasks,
    course,
    category,
    // step: this.state.step + 1,
    task: values,
    readingSource
  };
}
